import type { ConnectionConfig } from './connection';

/**
 * Command queue configuration
 */
export interface QueueConfig {
  /** Maximum concurrent commands (default: 1) */
  maxConcurrent: number;

  /** Default command timeout in ms (default: 30000) */
  defaultTimeoutMs: number;

  /** Enable command deduplication (default: false) */
  deduplicate: boolean;

  /** Queue capacity, 0 = unlimited (default: 100) */
  capacity: number;
}

/**
 * Backoff strategy types
 */
export type BackoffStrategy = 'linear' | 'exponential' | 'fibonacci';

/**
 * Retry configuration
 */
export interface RetryConfig {
  /** Maximum retry attempts (default: 3) */
  maxAttempts: number;

  /** Base delay between retries in ms (default: 1000) */
  baseDelayMs: number;

  /** Maximum retry delay in ms (default: 10000) */
  maxDelayMs: number;

  /** Backoff strategy (default: 'exponential') */
  backoff: BackoffStrategy;

  /** Jitter factor 0-1 (default: 0.1) */
  jitter: number;

  /** Error codes that should trigger retry */
  retryableErrors: string[];
}

/**
 * Log levels
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

/**
 * Logger interface
 */
export interface Logger {
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
}

/**
 * Logging configuration
 */
export interface LoggingConfig {
  /** Log level (default: 'warn') */
  level: LogLevel;

  /** Custom logger implementation */
  logger?: Logger;

  /** Include timestamps (default: true) */
  timestamps: boolean;

  /** Log WebSocket messages (default: false) */
  logMessages: boolean;
}

/**
 * Storage adapter types
 */
export type StorageType = 'memory' | 'indexeddb' | 'custom';

/**
 * Storage configuration
 */
export interface StorageConfig {
  /** Storage adapter type (default: 'memory') */
  type: StorageType;

  /** Database name for IndexedDB (default: 'ethbridge') */
  dbName?: string;

  /** Custom storage adapter instance */
  adapter?: unknown;
}

/**
 * Upload configuration
 */
export interface UploadConfig {
  /** Upload endpoint URL */
  endpoint: string;

  /** Chunk size in bytes (default: 65536 / 64KB) */
  chunkSizeBytes: number;

  /** Maximum concurrent uploads (default: 1) */
  maxConcurrent: number;

  /** Retry failed chunks (default: true) */
  retryFailedChunks: boolean;

  /** Maximum chunk retries (default: 3) */
  maxChunkRetries: number;
}

/**
 * Protocol configuration
 */
export interface ProtocolConfig {
  /** Protocol version */
  version: string;

  /** Enable request validation (default: true) */
  validateRequests: boolean;

  /** Enable response validation (default: true) */
  validateResponses: boolean;

  /** Request ID prefix (default: 'req_') */
  requestIdPrefix: string;
}

/**
 * Complete bridge configuration
 */
export interface BridgeConfig {
  /** WebSocket connection settings */
  connection: ConnectionConfig;

  /** Command queue settings */
  queue: QueueConfig;

  /** Protocol settings */
  protocol: ProtocolConfig;

  /** Retry strategy settings */
  retry: RetryConfig;

  /** Logging settings */
  logging: LoggingConfig;

  /** Storage adapter settings */
  storage?: StorageConfig;

  /** Upload settings */
  upload?: UploadConfig;
}

/**
 * Options for creating BridgeClient (partial config)
 */
export type BridgeClientOptions = Partial<{
  connection: Partial<ConnectionConfig>;
  queue: Partial<QueueConfig>;
  protocol: Partial<ProtocolConfig>;
  retry: Partial<RetryConfig>;
  logging: Partial<LoggingConfig>;
  storage: Partial<StorageConfig>;
  upload: Partial<UploadConfig>;
}>;

/**
 * Default queue configuration
 */
export const DEFAULT_QUEUE_CONFIG: QueueConfig = {
  maxConcurrent: 1,
  defaultTimeoutMs: 30000,
  deduplicate: false,
  capacity: 100,
};

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  backoff: 'exponential',
  jitter: 0.1,
  retryableErrors: [
    'DEVICE_DISCONNECTED',
    'DEVICE_BUSY',
    'TIMEOUT',
    'CONNECTION_ERROR',
  ],
};

/**
 * Default protocol configuration
 */
export const DEFAULT_PROTOCOL_CONFIG: ProtocolConfig = {
  version: '1.0',
  validateRequests: true,
  validateResponses: true,
  requestIdPrefix: 'req_',
};

/**
 * Default logging configuration
 */
export const DEFAULT_LOGGING_CONFIG: LoggingConfig = {
  level: 'warn',
  timestamps: true,
  logMessages: false,
};
